/**
 * i18n Configuration for Operator PWA
 *
 * Initializes i18next with:
 * - Hebrew (RTL) as primary target language after English
 * - Automatic language detection (localStorage, browser)
 * - Lazy loading of translation files
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Supported languages - only languages with complete translation files
// To add a new language: 1) Create files in /public/locales/{lang}/ 2) Add to this array
export const SUPPORTED_LANGUAGES = ['en', 'he', 'es', 'fr', 'de', 'ar', 'pt-BR', 'it'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Language display names (in their native language)
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  he: 'עברית',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  ar: 'العربية',
  'pt-BR': 'Português (BR)',
  it: 'Italiano',
};

// RTL languages
const RTL_LANGUAGES: string[] = ['he', 'ar'];

/**
 * Check if a language is RTL
 */
export function isRTL(language: string): boolean {
  return RTL_LANGUAGES.includes(language);
}

/**
 * Apply document direction based on language
 */
export function applyDocumentDirection(language: string): void {
  const dir = isRTL(language) ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = language;
}

// Initialize i18n
i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Language settings
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],

    // Namespace settings - operator-pwa specific
    defaultNS: 'common',
    ns: ['common', 'operator', 'errors'],

    // Detection settings
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    // Backend settings
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React escapes by default
    },

    // React settings
    react: {
      useSuspense: true,
    },

    // Development settings
    debug: import.meta.env.DEV,
  });

// Apply initial document direction
applyDocumentDirection(i18n.language);

// Update direction when language changes
i18n.on('languageChanged', (lng) => {
  applyDocumentDirection(lng);
});

export default i18n;
